import React, {useEffect, useState} from 'react';
import {
    Button,
    Card,
    Collapse,
    Descriptions,
    InputNumber,
    List,
    Modal,
    Space,
    Spin,
    Tag,
    Tooltip,
    Typography
} from "antd";
import {useRequest} from "ahooks";
import {CardProps} from "antd/lib/card";
import npiApi from "../../services/api";
import {DevCardTitle} from "./dev-dashboard";
import moment from "moment";
import {SearchOutlined} from "@ant-design/icons/lib";
import {IWaveLog} from "../../types/wave";
import {ReloadOutlined} from "@ant-design/icons";
import StepBackwardIcon from "@2fd/ant-design-icons/lib/StepBackward";
import StepForwardIcon from "@2fd/ant-design-icons/lib/StepForward";


export const WaveLogType = ({ status }: { status: string }) => {
    return <Tag color={status === "COMPLETED" ? "green" : status === "FAILED" ? "red" : "blue"}>
        {status}
    </Tag>
}

const JsonBlock: React.FC<{ data: unknown }> = ({ data }) => (
    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
    {JSON.stringify(data, null, 2)}
  </pre>
);

const NpiDashboardSyncWaveUpdateLogs = (props: CardProps) => {

    const {loading, data:logs, run} = useRequest<IWaveLog[], any>(npiApi.internal.dev.waveUpdate.logs, {
        onSuccess: (response) => {
            setHasMore(response.length >= 20)
        }
    });
    const {loading: loadingDetail, data:waveLogs=[], run: runDetail} = useRequest<IWaveLog[], any>(npiApi.internal.dev.waveUpdate.log, {manual: true});

    const [inputValue, setInputValue] = useState<number | null>(null);
    const [openId, setopenId] = useState<number | null>(null);
    const isModalOpen = openId !== null;

    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(false);


    const openDetail = (id: number) => {
        setopenId(id);
    };

    const closeDetail = () => {
        setopenId(null);
    };

    useEffect(() => {
        if (openId !== null) {
            runDetail(openId);
        }
    }, [openId, runDetail]);


    const reset = () => {
        run()
        setPage(1);
    }

    const fetchPrevious = () => {
        const newPage = page - 1;
        run({params: {page: newPage}})
        setPage(newPage)
    }

    const fetchNext = () => {
        const newPage = page + 1;
        run({params: {page: newPage}})
        setPage(newPage)
    }

    return <>
        <Card loading={loading} {...props} title={<DevCardTitle title={<Tooltip title={"Log only waves that have ever been launched at least once."}>
            Latest Wave updates
        </Tooltip>} onReload={reset}/>}>

            <div style={{width: "100%", marginBottom: 10, gap: 10, display: "flex"}}>
                <Space.Compact style={{flex: 1}}>
                    <InputNumber placeholder={"Wave ID"} style={{flex: 1}} value={inputValue} onChange={v => setInputValue(v)}/>
                    <Button type="primary" icon={<SearchOutlined />} disabled={!inputValue} onClick={() => openDetail(inputValue)}/>
                </Space.Compact>

                <Space.Compact>
                    <Button icon={<StepBackwardIcon />} disabled={page===1} onClick={() => fetchPrevious()}/>
                    <Button icon={<StepForwardIcon />} disabled={!hasMore} onClick={() => fetchNext()}/>
                </Space.Compact>

            </div>

            {!logs || logs.length === 0 ? (
                <div>Nothing to show...</div>
            ) : (
                <List
                    dataSource={logs}
                    renderItem={(item) => (
                        <List.Item
                            onClick={() => openDetail(item.wave_id)}
                            style={{ cursor: "pointer", flexDirection: "column", alignItems: "flex-start" }}
                        >
                            <Space>
                                <strong>#wave {item.wave_id}</strong>
                                {moment(item.created_at).format("YYYY-MM-DD HH:mm:ss")}
                                <WaveLogType status={item.type}/>
                            </Space>
                        </List.Item>
                    )}
                />
            )}
        </Card>

        <Modal
            open={isModalOpen}
            onCancel={closeDetail}
            title={
                <Space size={"large"}>
                    <strong>Wave #{openId}</strong>
                    <Button icon={<ReloadOutlined />} shape="circle" size={"small"} onClick={() => openId !== null && runDetail(openId)} disabled={loadingDetail}/>
                </Space>
            }
            width={900}
            destroyOnClose
            footer={null}
        >
            <Spin spinning={loadingDetail}>
                {waveLogs.length > 0 ? (
                    <Collapse accordion={true}>
                        {waveLogs.map((log, i) => (
                            <Collapse.Panel key={"log-"+i} header={
                                <Space>
                                    <strong>#{i+1}</strong>
                                    <WaveLogType status={log.type}/>
                                    {moment(log.created_at).format("YYYY-MM-DD HH:mm:ss")}
                                </Space>
                            }>
                                <Descriptions column={1} bordered size="small">
                                    <Descriptions.Item label="#log">{log.id}</Descriptions.Item>
                                    <Descriptions.Item label="Type">{log.type}</Descriptions.Item>
                                    <Descriptions.Item label="User ID">{log.user_id ?? '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Wave ID">{log.wave_id ?? '-'}</Descriptions.Item>
                                    <Descriptions.Item label="Created at">{moment(log.created_at).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
                                    <Descriptions.Item label="Metadata">
                                        <JsonBlock data={log.meta} />
                                    </Descriptions.Item>
                                </Descriptions>
                            </Collapse.Panel>
                        ))}
                    </Collapse>
                ) : (
                    <Typography.Text type="secondary">No data loaded yet.</Typography.Text>
                )}
            </Spin>
        </Modal>
    </>
};

export default NpiDashboardSyncWaveUpdateLogs;